/**
 * Registers the FitX PWA service worker for Trainer/Client product routes only.
 * Platform Admin (`/admin/*`) never activates product caching (PWA-SEC-01).
 * SW is served from `/sw.js` (copied to public/ on build) with scope `/`.
 */
import { isAdminSpaPath, purgePwaCaches } from '@/utils/pwaCache'

let registrationPromise = null
let waitingWorker = null

export function getPwaWaitingWorker() {
  return waitingWorker
}

export async function applyPwaUpdate() {
  if (waitingWorker) {
    waitingWorker.postMessage({ type: 'SKIP_WAITING' })
    waitingWorker = null
  }
  window.location.reload()
}

async function unregisterAllServiceWorkers() {
  if (!navigator.serviceWorker?.getRegistrations)
    return

  const registrations = await navigator.serviceWorker.getRegistrations()

  await Promise.all(registrations.map(r => r.unregister()))
}

/**
 * @returns {Promise<ServiceWorkerRegistration | null>}
 */
export async function registerFitxPwa() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator))
    return null

  if (isAdminSpaPath(window.location.pathname)) {
    await unregisterAllServiceWorkers()
    await purgePwaCaches()

    return null
  }

  if (registrationPromise)
    return registrationPromise

  registrationPromise = (async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' })

      const trackWaiting = worker => {
        if (!worker)
          return
        waitingWorker = worker
        window.dispatchEvent(new CustomEvent('fitx:pwa-update-available'))
      }

      if (registration.waiting)
        trackWaiting(registration.waiting)

      registration.addEventListener('updatefound', () => {
        const installing = registration.installing
        if (!installing)
          return
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller)
            trackWaiting(installing)
        })
      })

      setInterval(() => {
        registration.update().catch(() => {})
      }, 60 * 60 * 1000)

      return registration
    }
    catch (_) {
      registrationPromise = null

      return null
    }
  })()

  return registrationPromise
}

export default function (app) {
  if (import.meta.env.MODE === 'test')
    return

  // Defer so first paint is not blocked (F-025 §7).
  if (typeof window !== 'undefined') {
    window.addEventListener('load', () => {
      registerFitxPwa().catch(() => {})
    })
  }

  app.config.globalProperties.$pwa = {
    applyUpdate: applyPwaUpdate,
  }
}
